import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useConnectedStudents, useCounselorSessionHistory } from '@/hooks/useCounselorData';

export interface StudentSearchResult {
  type: 'student';
  id: string;
  title: string;
  subtitle: string;
  additionalInfo?: string;
  data: any;
}

export interface SessionSearchResult {
  type: 'session';
  id: string;
  title: string;
  subtitle: string;
  additionalInfo?: string;
  data: any;
}

export type SearchResult = StudentSearchResult | SessionSearchResult;

export interface UseSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isLoading: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  studentResults: StudentSearchResult[];
  sessionResults: SessionSearchResult[];
  hasResults: boolean;
}

export function useSearch(): UseSearchReturn {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Fetch connected students and session history
  const { data: studentsData, isLoading: studentsLoading } = useConnectedStudents(user?.id, {
    page: 1,
    limit: 100
  });

  const { data: sessionsData, isLoading: sessionsLoading } = useCounselorSessionHistory(user?.id, {
    page: 1,
    limit: 100
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Update isOpen based on query
  useEffect(() => {
    if (query.trim().length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [query]);

  // Search and filter results
  const results = useMemo(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      return [];
    }

    const searchTerm = debouncedQuery.toLowerCase();
    const allResults: SearchResult[] = [];

    // Search students
    if (studentsData?.students) {
      const studentResults = studentsData.students
        .filter((student: any) => {
          if (!student) return false;
          
          const nameMatch = (`${student.firstName} ${student.lastName}`).toLowerCase().includes(searchTerm);
          const emailMatch = student.email?.toLowerCase().includes(searchTerm);
          const phoneMatch = student.phone?.toLowerCase().includes(searchTerm);
          const schoolMatch = student.schoolName?.toLowerCase().includes(searchTerm);
          const interestsMatch = student.interests?.some((interest: string) => 
            interest.toLowerCase().includes(searchTerm)
          );
          
          return nameMatch || emailMatch || phoneMatch || schoolMatch || interestsMatch;
        })
        .slice(0, 5) // Limit to 5 results
        .map((student: any): StudentSearchResult => ({
          type: 'student',
          id: student.id,
          title: `${student.firstName} ${student.lastName}`,
          subtitle: `${student.grade ? `Grade ${student.grade}` : 'Student'} • ${student.schoolName || 'No school'}`,
          additionalInfo: `${student.totalSessions || 0} sessions • ${student.status}`,
          data: student,
        }));

      allResults.push(...studentResults);
    }

    // Search sessions
    if (sessionsData && typeof sessionsData === 'object' && 'sessions' in sessionsData && Array.isArray(sessionsData.sessions)) {
      const sessionResults = sessionsData.sessions
        .filter((session: any) => {
          if (!session) return false;
          
          const studentNameMatch = session.student ? 
            (`${session.student.name || ''}`).toLowerCase().includes(searchTerm) : false;
          const sessionTypeMatch = session.sessionType?.toLowerCase().includes(searchTerm);
          const statusMatch = session.status?.toLowerCase().includes(searchTerm);
          const feedbackMatch = session.feedback?.toLowerCase().includes(searchTerm);
          
          return studentNameMatch || sessionTypeMatch || statusMatch || feedbackMatch;
        })
        .slice(0, 5) // Limit to 5 results
        .map((session: any): SessionSearchResult => ({
          type: 'session',
          id: session.id,
          title: `${session.sessionType?.toUpperCase() || 'Session'} with ${session.student?.name || 'Student'}`,
          subtitle: `${session.status?.toUpperCase()} • ${new Date(session.startedAt).toLocaleDateString()}`,
          additionalInfo: session.rating ? `${session.rating}★ rating` : 'No rating',
          data: session,
        }));

      allResults.push(...sessionResults);
    }

    return allResults;
  }, [debouncedQuery, studentsData, sessionsData]);

  // Separate results by type
  const studentResults = results.filter(result => result.type === 'student') as StudentSearchResult[];
  const sessionResults = results.filter(result => result.type === 'session') as SessionSearchResult[];

  return {
    query,
    setQuery,
    results,
    isLoading: studentsLoading || sessionsLoading,
    isOpen,
    setIsOpen,
    studentResults,
    sessionResults,
    hasResults: results.length > 0,
  };
}